import { Context, Effect } from "effect"
import type { QueuePublishError, QueueSubscribeError, WorkflowAlreadyStartedError } from "./errors.ts"
import type { TopicRegistry as TR } from "./topic-registry.ts"
import type { WorkflowRegistry as WR } from "./workflow-registry.ts"

export type TopicRegistry = TR
export { TOPIC_NAMES } from "./topic-registry.ts"
export type QueueName = keyof TopicRegistry & string
export type TaskName<T extends QueueName> = string & keyof TopicRegistry[T]
export type TaskPayload<T extends QueueName, K extends TaskName<T>> = TopicRegistry[T][K]
export type TaskHandlers<T extends QueueName> = {
  [K in TaskName<T>]: (payload: TopicRegistry[T][K]) => Effect.Effect<void, unknown>
}

export type WorkflowRegistry = WR
export { WORKFLOW_NAMES } from "./workflow-registry.ts"
export type WorkflowName = keyof WorkflowRegistry & string
export type WorkflowInput<W extends WorkflowName> = WorkflowRegistry[W]

/**
 * Starts workflows by id. Workflow id is the dedupe key (analogous to
 * BullMQ's `dedupeKey`) — Temporal guarantees that two actively running
 * workflows with the same id are never possible.
 *
 * `start` fails loudly when a workflow with the same id is already running:
 * the underlying Temporal error propagates so callers can't mistake a
 * deduped call for a fresh start. Callers that may legitimately retry for
 * the same id should either query workflow state first or catch and handle
 * the already-running case explicitly.
 *
 * `signalWithStart` is idempotent against running workflows by design:
 * Temporal delivers the signal to the existing run if one exists, otherwise
 * it starts a new one. Coalescing rapid signals is the workflow's
 * responsibility.
 */
export interface WorkflowStarterShape {
  readonly start: <W extends WorkflowName>(
    workflow: W,
    input: WorkflowInput<W>,
    options: { readonly workflowId: string; readonly startDelayMs?: number },
  ) => Effect.Effect<void, WorkflowAlreadyStartedError>
  readonly signalWithStart: <W extends WorkflowName>(
    workflow: W,
    input: WorkflowInput<W>,
    options: {
      readonly workflowId: string
      readonly signal: string
      readonly signalArgs?: readonly unknown[]
    },
  ) => Effect.Effect<void>
}

export class WorkflowStarter extends Context.Service<WorkflowStarter, WorkflowStarterShape>()(
  "@domain/queue/WorkflowStarter",
) {}

export type WorkflowExecutionStatus =
  | "running"
  | "completed"
  | "failed"
  | "canceled"
  | "terminated"
  | "continued-as-new"
  | "timed-out"
  | "paused"
  | "unknown"

// Statuses a workflow can never leave — the execution is done and will not
// advance further. `running` / `paused` / `continued-as-new` are still live,
// and `unknown` is treated as not-terminal (conservative: don't assume done).
const TERMINAL_WORKFLOW_STATUSES = new Set<WorkflowExecutionStatus>([
  "completed",
  "failed",
  "canceled",
  "terminated",
  "timed-out",
])

/** Whether a workflow execution has reached a final state (won't progress). */
export const isTerminalWorkflowStatus = (status: WorkflowExecutionStatus): boolean =>
  TERMINAL_WORKFLOW_STATUSES.has(status)

export type WorkflowDescription = {
  readonly status: WorkflowExecutionStatus
  readonly runId: string
  readonly startTime: Date
  readonly closeTime: Date | null
  readonly failure: string | null
}

export interface WorkflowQuerierShape {
  readonly describe: (workflowId: string) => Effect.Effect<WorkflowDescription | null>
  readonly query: <T>(workflowId: string, queryName: string) => Effect.Effect<T | null>
}

export class WorkflowQuerier extends Context.Service<WorkflowQuerier, WorkflowQuerierShape>()(
  "@domain/queue/WorkflowQuerier",
) {}

/**
 * Whether the workflow with `workflowId` is still live — it exists and hasn't
 * reached a terminal status. A missing workflow (never durably started, or
 * already GC'd) counts as not alive.
 */
export const isWorkflowAliveUseCase = (workflowId: string): Effect.Effect<boolean, never, WorkflowQuerier> =>
  Effect.gen(function* () {
    const querier = yield* WorkflowQuerier
    const description = yield* querier.describe(workflowId)
    return description !== null && !isTerminalWorkflowStatus(description.status)
  })

export interface PublishOptions {
  readonly dedupeKey?: string
  /**
   * Debounce window in ms. Each publish within the window extends the TTL
   * and replaces the pending payload, so the task fires after `debounceMs`
   * of quiet on this `dedupeKey`. Appropriate when you want to wait for a
   * stream of events to settle (e.g. `trace-end:run` after `TracesIngested`).
   *
   * Mutually exclusive with `throttleMs`, `latestThrottleMs` and `leadingThrottleMs`.
   */
  readonly debounceMs?: number
  /**
   * Throttle window in ms. The first publish schedules the task for
   * `now + throttleMs`; subsequent publishes within the window are dropped
   * (clock not extended, payload not replaced). Guarantees a hard upper
   * bound of `throttleMs` on fire latency from the first publish, plus a
   * maximum rate of one fire per `throttleMs` per `dedupeKey`. Appropriate
   * when you want a predictable "at most once per N" cadence even under a
   * constant flow of publishes (e.g. annotation-driven alignment refresh).
   *
   * Requires `dedupeKey`. Mutually exclusive with `debounceMs`, `latestThrottleMs` and `leadingThrottleMs`.
   */
  readonly throttleMs?: number
  /**
   * Latest-throttle window in ms. The first publish schedules the task for
   * `now + latestThrottleMs`; subsequent publishes within the window replace
   * the pending payload without extending the fire time. Guarantees a hard
   * upper bound of `latestThrottleMs` on fire latency from the first publish,
   * while using the latest payload seen in that window.
   *
   * Requires `dedupeKey`. Mutually exclusive with `debounceMs`, `throttleMs` and `leadingThrottleMs`.
   */
  readonly latestThrottleMs?: number
  /**
   * Leading-edge throttle window in ms. The first publish fires the task
   * *immediately* (no delay), then suppresses subsequent publishes for
   * `leadingThrottleMs` per `dedupeKey` (clock not extended, payload not
   * replaced). Same "at most once per N" rate as `throttleMs`, but the run
   * happens at the *start* of the window instead of the end.
   *
   * Use this when the task evaluates a trailing time window ending at run
   * time: with `throttleMs` the `throttleMs` delay slides that window past
   * the very activity that triggered the publish, so a discrete burst can be
   * missed (the saved-search monitor firing check). Firing on the leading
   * edge keeps the triggering activity inside the evaluated window.
   *
   * Requires `dedupeKey`. Mutually exclusive with `debounceMs`, `throttleMs` and `latestThrottleMs`.
   */
  readonly leadingThrottleMs?: number
  /**
   * Total attempts BullMQ should make before the job is considered failed
   * (inclusive of the first try). Set alongside `backoff` to get bounded
   * exponential retry for transient dependency failures (e.g. Temporal
   * unavailability). Without a `backoff` set, retries fire immediately.
   */
  readonly attempts?: number
  /**
   * Exponential backoff between retry attempts, in milliseconds. Delay is
   * `delayMs * 2^(attempt-1)`. Ignored when `attempts` is unset or ≤ 1.
   */
  readonly backoff?: {
    readonly type: "exponential"
    readonly delayMs: number
  }
}

/**
 * Options for `scheduleRepeatable`. `key` is the scheduler id — calling
 * `scheduleRepeatable` again with the same key replaces the existing
 * schedule, which makes it safe to register on every worker boot.
 * `pattern` is a cron expression; `tz` defaults to `"UTC"`.
 */
export interface ScheduleRepeatableOptions {
  readonly key: string
  readonly pattern: string
  readonly tz?: string
}

export interface QueuePublisherShape {
  readonly publish: <T extends QueueName, K extends TaskName<T>>(
    queue: T,
    task: K,
    payload: TaskPayload<T, K>,
    options?: PublishOptions,
  ) => Effect.Effect<void, QueuePublishError>
  /**
   * Register (or replace) a recurring schedule that publishes `task` on
   * `queue` at every cron tick. Idempotent by `options.key` — re-running
   * with the same key updates the schedule in place.
   */
  readonly scheduleRepeatable: <T extends QueueName, K extends TaskName<T>>(
    queue: T,
    task: K,
    payload: TaskPayload<T, K>,
    options: ScheduleRepeatableOptions,
  ) => Effect.Effect<void, QueuePublishError>
  readonly close: () => Effect.Effect<void>
}

export class QueuePublisher extends Context.Service<QueuePublisher, QueuePublisherShape>()(
  "@domain/queue/QueuePublisher",
) {}

/** Attempt accounting for a terminally-failed job, passed to {@link FinalFailureHandlers}. */
export interface FinalFailureContext {
  readonly attemptsMade: number
  readonly attemptsConfigured: number
}

/**
 * Per-task hooks that run once a job has exhausted its BullMQ attempts (the
 * terminal failure, not every failed attempt). Best-effort: they run outside
 * the job lifecycle, so a hook that throws is logged, not retried. Use for
 * accounting that must survive retry exhaustion (e.g. incrementing a failure
 * counter). The hook Effect must be self-contained — it runs against the
 * consumer's base context, so provide its own layers.
 */
export type FinalFailureHandlers<T extends QueueName> = {
  [K in TaskName<T>]?: (
    payload: TopicRegistry[T][K],
    error: Error,
    context: FinalFailureContext,
  ) => Effect.Effect<void, unknown>
}

export interface SubscribeOptions<T extends QueueName = QueueName> {
  readonly concurrency?: number
  readonly onFinalFailure?: FinalFailureHandlers<T>
}

export interface QueueConsumer {
  readonly start: () => Effect.Effect<void, QueueSubscribeError>
  readonly stop: () => Effect.Effect<void>
  readonly subscribe: <T extends QueueName>(queue: T, handlers: TaskHandlers<T>, options?: SubscribeOptions<T>) => void
}

export { QueueClientError, QueuePublishError, QueueSubscribeError, WorkflowAlreadyStartedError } from "./errors.ts"
